import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { hoyLocal } from '../utils/fecha'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CFormSelect, CButton, CAlert, CBadge,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CCard, CCardHeader, CCardBody, CTooltip,
  CTabs, CTabList, CTab, CTabContent, CTabPanel, CSpinner,
  CInputGroup, CInputGroupText,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilFile, cilUser, cilSettings, cilDollar, cilCalendar, cilChart, cilPencil, cilX,
  cilPrint,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { normalizarNumeroTexto } from '../utils/numeros'
import { fmtMoneda, simboloMoneda } from '../utils/moneda'
import { numeroWhatsApp } from '../utils/whatsapp'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import EstadoPrestamoBadge from '../components/EstadoPrestamoBadge'
import WwSearchField from '../components/ww/WwSearchField'
import { usePendiente as usePending } from '../components/ww/pendiente'



// Columnas de los tabs del préstamo seleccionado (arptextracto.cs:804-807,
// arptcontrato.cs:723-725 — réplica de los cursores GX)
const columnasTabAbonos: WwColumn[] = [
  { key: 'abonoNro', title: 'Nro', type: 'number' },
  { key: 'abonoFecha', title: 'Fecha', type: 'date' },
  { key: 'abonoTipoAbono', title: 'Tipo Abono' },
  { key: 'abonoValor', title: 'Valor', type: 'currency' },
  { key: 'abonoPrestamos', title: 'Préstamos', type: 'number' },
  { key: 'abonoCondenas', title: 'Condenas', type: 'number' },
]
const columnasTabAtrasos: WwColumn[] = [
  { key: 'atrazoNro', title: 'Nro', type: 'number' },
  { key: 'atrazoFecha', title: 'Fecha', type: 'date' },
]

// Formato de moneda: símbolo configurado por empresa + máscara GX
// "$ Z,ZZZ,ZZZ,ZZ9.99" (arptcortecajadiario.cs:184; coma de miles y punto
// decimal) — fuente única utils/moneda.ts (fmtMoneda). [RGM] 2026-10-02 (F)

// [RGM] 2026-10-01 (faltantes-multitema F): fecha SOLO (YYYY-MM-DD) para los
// inputs type="date" — el grid trae ISO completo ("2026-10-01T00:00:00") y el
// input muestra el "time con ceros" si no se recorta. Mismo formato que
// hoyLocal() (../utils/fecha — yyyy-MM-dd local).
const soloFecha = (v: any, fallback: string): string => {
  if (v === null || v === undefined || v === '') return fallback
  return String(v).slice(0, 10)
}

// Labels GX de los filtros (clienteprestamowc.cs:2971-2974 tipos;
// 2994-2998 modalidades 1=DIARIO,7=SEMANAL,15=QUINCENAL,30=MENSUAL,60=LIBRE).
// Los tipos en BD son char(n): se normalizan con trim()+toUpperCase().
const TIPO_LABELS: Record<string, string> = {
  NUEVO: 'NUEVO',
  RENOVACION: 'RENOVACION/REENGANCHE',
  RELIQUIDADO: 'RELIQUIDADO/PASADO',
  CLONADO: 'CLONADO',
}
const METODO_PAGO_LABELS: Record<string, string> = {
  '1': 'DIARIO',
  '7': 'SEMANAL',
  '15': 'QUINCENAL',
  '30': 'MENSUAL',
  '60': 'LIBRE',
}

// [RGM] Factura Nro (vínculo factura→préstamo, PrestamoFacturaNro): se muestra
// SOLO con el módulo de facturación activo (CajasPage) y con valor > 0
// (celda vacía cuando el préstamo no tiene factura).
const columnaFacturaNro: WwColumn = {
  key: 'prestamoFacturaNro', title: 'Factura Nro', type: 'number',
  render: (row) => Number(row.prestamoFacturaNro ?? 0) > 0 ? Number(row.prestamoFacturaNro) : '',
}

const PrestamosPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = currentUser()
  const company = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const { aviso: notice, setAviso: setNotice } = usePending()
  const formRef = useRef<HTMLFormElement>(null)
  // Revert del input Forzar Saldo en error (remount por key — patrón Base
  // INLINE de RutasPage.tsx:106-108)
  const saldoRev = useRef(0)
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null)
  const [editing, setEditing] = useState(false)
  const [editingNro, setEditingNro] = useState<number | null>(null)
  const [history, setHistory] = useState<any>(null)
  // Filtro de ruta venido desde /rutas (rowAction 'Prestamos')
  const [routeFilter, setRouteFilter] = useState(() => searchParams.get('ruta') ?? '')
  // ── Selección de préstamo (colapso + tabs — patrón Empresas superadmin) ──
  const [seleccionado, setSeleccionado] = useState<any | null>(null)
  const [tabExtracto, setTabExtracto] = useState<any | null>(null)
  const [tabContrato, setTabContrato] = useState<any | null>(null)
  const [cargandoTabs, setCargandoTabs] = useState(false)
  // ── Barra de filtros rápidos (wireframe aprobado; filtros del WW GX
  //    prestamoww.cs:230 simplificados: estado, ruta, cobrador, tipo,
  //    modalidad). La búsqueda general vive en el filtro principal del
  //    WwGrid (placeholder con indicaciones + lupa). ──
  const [filtroEstado, setFiltroEstado] = useState('')  // '' = Todos
  const [filtroRuta, setFiltroRuta] = useState('')
  const [filtroCobrador, setFiltroCobrador] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroModalidad, setFiltroModalidad] = useState('')
  // ── Modo del WW prestamoww por Condicion (validado en KB GX):
  //    Condicion == 0 → GESTIÓN AVANZADA: muestra TODOS los préstamos y la
  //    columna Forzar Saldo es visible (prestamoww.cs:3147-3153, "Attribute")
  //    + columna "Nuevo Saldo" editable (:3402).
  //    Condicion == 1 → NORMAL: solo activos (WHERE Saldo > 0 o condenas
  //    pendientes ⇔ SaldoReal > 0 — prestamoww.cs:7001-7004) y Forzar Saldo
  //    queda "Invisible". ──
  // Condicion del WW: ?avanzada=1 → Gestión Avanzada (Condicion=0 GX)
  const [gestionAvanzada, setGestionAvanzada] = useState(
    () => searchParams.get('avanzada') === '1')
  const [rutas, setRutas] = useState<string[]>([])
  // [RGM] 2026-09-17 (reporte Rafael "no me está mostrando el cobrador"): la
  // lista COMPLETA de rutas (con cobradorNombre) — el campo "Cobrador" del
  // form sigue a la RUTA del préstamo (GX: prestamo.cs:881-888).
  const [rutasDetalle, setRutasDetalle] = useState<any[]>([])
  const [cobradores, setCobradores] = useState<string[]>([])
  // Estados de dominio del servidor (GET /prestamos/estados — app ONLINE
  // pura, sin respaldo local: si el servidor no responde, no hay botones)
  const [estadosDominio, setEstadosDominio] = useState<string[]>([])
  // Precarga desde /facturas (rowAction 'Crear Prestamo'): cliente, valor a
  // financiar y la FACTURA vinculada (facturaNro) — el Nro viaja OCULTO en el
  // alta y se persiste en PrestamoFacturaNro (panel 'prestar' del GX).
  const preloadRef = useRef<{ clienteCedula: string; valor: string; facturaNro: string } | null>((() => {
    const cliente = searchParams.get('cliente')
    if (!cliente) return null
    return {
      clienteCedula: cliente,
      valor: searchParams.get('valor') ?? '',
      facturaNro: searchParams.get('facturaNro') ?? '',
    }
  })())
  // [RGM] 2026-10-02 (reporte Rafael): la modalidad arranca en Diario (1) —
  // primer ítem del combo GX (prestamo.cs:6639-6645, DIARIO:1..LIBRE:60).
  const [form, setForm] = useState({
    empresaCodigo: company, clienteCedula: '', clienteNombre: '', metodoPago: 1, tipo: 'NUEVO',
    valor: '', cuotas: '', cuota: '', fechaInicia: hoyLocal(),
    // [RGM] 2026-09-23 (pedido Rafael): "Fecha Prestamo" editable para el
    // administrador (WW GX: edtPrestamoFecha) — separada de Fecha Inicia Pagos.
    fechaPrestamo: hoyLocal(),
    fechaProximoCobro: '', zonaCodigo: 0,
    prestamoDiaCobro: '', porcentaje: 0,
    aceptaCondenas: 'No', nroAtrasosPorCondena: '', valorCondenaDiferenteA: 0, valorCondena: 0, feriados: '',
    prestamoLibreValor1: 0, prestamoLibreValor2: 0, prestamoLibreValor3: 0,
    prestamoLibreNumerico1: 0, prestamoLibreNumerico2: 0, prestamoLibreNumerico3: 0,
    prestamoLibreCaracter1: '', prestamoLibreCaracter2: '',
    prestamoObservacion: '', verificado: '',
  })

  // Factura vinculada al crédito en el ALTA (0 = préstamo normal). Viaja
  // OCULTA en el POST (panel 'prestar' GX) — no se muestra campo en el form.
  const [facturaNroCrear, setFacturaNroCrear] = useState(0)
  // Módulo de facturación de la empresa (EmpresaModuloFacturacion): la columna
  // "Factura Nro" solo se muestra si está activo (patrón CajasPage: GET /cuenta).
  const [modFacturacion, setModFacturacion] = useState(false)
  // [RGM] 2026-10-02: flag de la empresa — trabaja festivos → campo Feriados oculto.
  const [trabajaFestivo, setTrabajaFestivo] = useState(false)

  const openNew = () => {
    setForm({
      empresaCodigo: company, clienteCedula: '', clienteNombre: '', metodoPago: 1, tipo: 'NUEVO',
      valor: '', cuotas: '', cuota: '', fechaInicia: hoyLocal(),
      fechaPrestamo: hoyLocal(),
      fechaProximoCobro: '', zonaCodigo: 0,
      prestamoDiaCobro: '', porcentaje: 0,
      aceptaCondenas: 'No', nroAtrasosPorCondena: '', valorCondenaDiferenteA: 0, valorCondena: 0, feriados: '',
      prestamoLibreValor1: 0, prestamoLibreValor2: 0, prestamoLibreValor3: 0,
      prestamoLibreNumerico1: 0, prestamoLibreNumerico2: 0, prestamoLibreNumerico3: 0,
      prestamoLibreCaracter1: '', prestamoLibreCaracter2: '',
      prestamoObservacion: '', verificado: '',
    })
    setSelectedCustomer(null)
    setEditing(false)
    setEditingNro(null)
    setHistory(null)
    setError('')
    // Precarga venida desde /facturas (rowAction 'Crear Prestamo'): cliente +
    // valor a financiar + factura vinculada (oculta en el POST).
    if (preloadRef.current) {
      const { clienteCedula, valor, facturaNro } = preloadRef.current
      setForm((f) => ({ ...f, clienteCedula, valor }))
      setFacturaNroCrear(Number(facturaNro) || 0)
      api
        .get<any>(`/clientes/${clienteCedula}?empresa=${company}`)
        .then((detail) => {
          setSelectedCustomer(detail)
          // [RGM] 2026-10-05 (reporte Rafael "no existe ruta" al crear desde
          // factura): el load del BC prestamo trae la RUTA del cliente
          // (prestamo_bc.cs:66-94) — mismo patrón de selectCustomer (abajo).
          // Sin esto el alta salía con zonaCodigo=0 → el backend no resolvía
          // la zona del préstamo.
          setForm((f) => ({ ...f, zonaCodigo: Number(detail?.zonaCodigo ?? 0) || f.zonaCodigo }))
        })
        .catch(() => {})
      preloadRef.current = null
      setSearchParams({}, { replace: true })
    } else {
      // Alta normal: sin factura vinculada.
      setFacturaNroCrear(0)
    }
    setModalOpen(true)
  }

  // [RGM] 2026-10-05 (pedido Rafael — factura a crédito): al llegar desde
  // /facturas con el preload (cliente + valor + facturaNro), el modal se abre
  // SOLO, prellenado — antes el preload solo se consumía al pulsar "Agregar".
  // openNew() limpia el ref (→ no reabre; guarda para StrictMode, que monta
  // los efectos dos veces en dev) y quita los query params.
  useEffect(() => {
    if (preloadRef.current) openNew()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Datos de dominio de la barra de filtros (prestamowwgetfilterdata.cs:25 —
  // el proc GX expone los valores de los filtros del WW: TFZonaNombre
  // prestamowwgetfilterdata.cs:258, TFClienteNombre :242; los estados son
  // los del atributo de fórmula — vencido = arptpasadosdefecha.cs:288).
  useEffect(() => {
    api.get<any[]>('/rutas?empresa=' + company)
      .then((r) => {
        setRutas([...new Set(r.map((x: any) => String(x.zonaNombre ?? '').trim()).filter(Boolean))])
        setRutasDetalle(r)
      })
      .catch(() => undefined)
    api.get<any[]>('/cobradores?empresa=' + company)
      .then((cb) => setCobradores([...new Set(cb.map((x: any) => String(x.usuario ?? '').trim()).filter(Boolean))]))
      .catch(() => undefined)
    api.get<string[]>('/prestamos/estados')
      .then(setEstadosDominio)
      .catch(() => undefined)
    // Módulo de facturación de la empresa: columna "Factura Nro" condicional
    // (EmpresaModuloFacturacion — mismo patrón de CajasPage).
    // [RGM] 2026-10-02: también el flag TrabajaFestivo — con la empresa que
    // trabaja festivos, el campo Feriados del form se OCULTA.
    api.get<any>('/cuenta?empresa=' + company)
      .then((cuenta) => {
        // [RGM] 2026-10-07 (pedido Rafael): la columna "Factura Nro" también
        // aplica en motor SOLO FACTURACIÓN (módulo pendiente de pago incluido).
        setModFacturacion(Number(cuenta.moduloFacturacion ?? 0) === 1
          || String(cuenta.modo ?? '') === 'FACTURACION')
        setTrabajaFestivo(cuenta.trabajaFestivo === true)
        // [RGM] 2026-10-07 (diseño Rafael B+C): en modo SOLO FACTURACIÓN la
        // pantalla se titula "Créditos" (las cuentas por cobrar del vendedor).
        setModoCtv(String(cuenta.modo ?? 'PRESTAMOS'))
      })
      .catch(() => undefined)
  }, [company])

  const load = () => {
    setLoading(true)
    api.get<any[]>(`/prestamos?empresa=${company}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [company])

  // Filtro de ruta venido desde /rutas: se mantiene en sync con el query param.
  useEffect(() => {
    setRouteFilter(searchParams.get('ruta') ?? '')
  }, [searchParams])

  // [RGM] 2026-10-01 (reporte Rafael "Modificar no funciona en el view"):
  // el botón Modificar de PrestamoViewPage llega con ?editar=<nro>; cuando las
  // filas cargan se abre el modal de edición de ese préstamo y se limpia el
  // param (handleEdit → PUT /prestamos/{nro}).
  useEffect(() => {
    const editar = searchParams.get('editar')
    if (!editar || rows.length === 0) return
    const fila = rows.find((r) => String(r.prestamoNro ?? '') === editar)
    if (fila) {
      handleEdit(fila)
      setSearchParams({}, { replace: true })
    }
  }, [rows, searchParams, setSearchParams])

  const visibleRows = useMemo(() => {
    const tipo = filtroTipo ? filtroTipo.toUpperCase() : ''
    return rows.filter((row) => {
      // Modo normal: SOLO activos (prestamoww.cs:7003 — Saldo > 0 o
      // condenas pendientes ⇔ SaldoReal > 0). Gestión avanzada: todos.
      if (!gestionAvanzada && Number(row.saldoReal ?? 0) <= 0) return false
      if (routeFilter && String(row.ruta ?? '').trim() !== routeFilter.trim()) return false
      if (filtroEstado && (row.estadoCalc ?? '') !== filtroEstado) return false
      if (filtroRuta && String(row.ruta ?? '').trim() !== filtroRuta) return false
      if (filtroCobrador && String(row.cobrador ?? '').trim() !== filtroCobrador) return false
      if (tipo && String(row.prestamoTipo ?? '').trim().toUpperCase() !== tipo) return false
      if (filtroModalidad && String(row.prestamoMPago ?? '') !== filtroModalidad) return false
      return true
    })
  }, [rows, gestionAvanzada, routeFilter, filtroEstado, filtroRuta, filtroCobrador, filtroTipo, filtroModalidad])

  const filtrosActivos = [
    routeFilter ? { clave: 'ruta-param', label: `Ruta: ${routeFilter}` } : null,
    filtroEstado ? { clave: 'estado', label: `Estado: ${filtroEstado}` } : null,
    filtroRuta ? { clave: 'ruta', label: `Ruta: ${filtroRuta}` } : null,
    filtroCobrador ? { clave: 'cobrador', label: `Cobrador: ${filtroCobrador}` } : null,
    filtroTipo ? { clave: 'tipo', label: `Tipo: ${TIPO_LABELS[filtroTipo.toUpperCase()] ?? filtroTipo}` } : null,
    filtroModalidad ? { clave: 'modalidad', label: `Modalidad: ${METODO_PAGO_LABELS[filtroModalidad] ?? filtroModalidad}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]

  // Columnas con Forzar Saldo INLINE (el render usa saldoRev/guardarSaldoForzado
  // del componente — por eso se construye aquí, como RutasPage.tsx:126)
  // Forzar Saldo INLINE: misma interacción que la Base INLINE de
  // RutasPage (input no controlado + blur/Enter guarda). Lógica réplica de
  // forzarsaldo.cs:60-80 (UPDATE PrestamoSaldo sin recalcular nada; el WW GX
  // lo advierte: "Forzar Cambiar Saldo, Su Contabilidad será Errónea" —
  // prestamoww.cs:4744).
  const guardarSaldoForzado = useCallback((fila: any, valor: string) => {
    const nuevo = Number(valor)
    if (Number.isNaN(nuevo) || nuevo === Number(fila.saldoReal)) return
    api
      .put(`/prestamos/${fila.prestamoNro}/forzar-saldo`, {
        empresaCodigo: company, saldo: nuevo,
      })
      .then(() => {
        setRows((prev) => prev.map((r) => (r.prestamoNro === fila.prestamoNro ? { ...r, prestamoSaldo: nuevo } : r)))
      })
      .catch((e: any) => {
        setError(e.message)
        saldoRev.current += 1
      })
  }, [company])


  const columns = useMemo<WwColumn[]>(() => [

  { key: 'prestamoNro', title: 'Nro', type: 'number', link: true },
  // Factura Nro: solo con el módulo de facturación activo en la empresa.
  ...(modFacturacion ? [columnaFacturaNro] : []),
  {
    key: 'estadoCalc', title: 'Estado',
    render: (row) => <EstadoPrestamoBadge estado={row.estadoCalc} />,
  },
  {
    // [RGM] 2026-09-23 (pedido Rafael): badge Al día / En mora — días de mora
    // GX (PrestamoDiasMora, el mismo dato del móvil).
    key: 'diasMora', title: 'Mora', minWidth: 110,
    render: (row) => Number(row.diasMora ?? 0) > 0
      ? <CBadge color="danger">En mora {Number(row.diasMora)}</CBadge>
      : <CBadge color="success">Al día</CBadge>,
  },
  { key: 'prestamoFecha', title: 'Fecha', type: 'date' },
  { key: 'clienteCedula', title: 'Cedula' },
  { key: 'clienteNombre', title: 'Nombre' },
  { key: 'clienteDireccion', title: 'Direccion' },
  { key: 'ruta', title: 'Ruta' },
  { key: 'whatsApp', title: 'WhatsApp' },
  {
    key: 'prestamoTipo', title: 'Tipo',
    // [RGM] 2026-09-17 (pedido Rafael): el tipo se muestra en MAYÚSCULA
    // (GX: NUEVO / RENOVACION) — también se guarda en mayúscula.
    render: (row) => String(row.prestamoTipo ?? '').trim().toUpperCase(),
  },
  {
    key: 'prestamoMPago', title: 'Modalidad',
    // [RGM] 2026-09-17 (pedido Rafael): TEXTO de la modalidad (labels GX:
    // 1=DIARIO .. 60=LIBRE) — el número no se muestra.
    render: (row) => METODO_PAGO_LABELS[String(Number(row.prestamoMPago))] ?? String(row.prestamoMPago ?? '').trim(),
  },
  {
    key: 'prestamoDiaCobro',
    title: 'Dia Cobro',
    // Etiqueta DESCRIPTIVA (como el selector del form): QT=15-30, 52=5-20,
    // 38=3-18, 10=DECADAL; 15/30/Lu..Do se muestran igual (el código interno
    // no se muestra en la grilla). Vacío = "(Ninguno)" (GX_EmptyItemText —
    // prestamo.cs:6653).
    render: (row) => {
      const v = String(row.prestamoDiaCobro ?? '').trim()
      if (!v) return '(Ninguno)'
      return { QT: '15-30', '52': '5-20', '38': '3-18', '10': 'DECADAL' }[v] ?? v
    },
  },
  { key: 'prestamoValor', title: 'Valor', type: 'currency' },
  {
    // [RGM] 2026-09-23 (pedido Rafael): cuotas abonadas/préstamo en UNA sola
    // columna, como el móvil ("3/15 cuotas pagas").
    key: 'cuotasPagas', title: 'Pagas', minWidth: 90,
    render: (row) => `${Number(row.cuotasPagas ?? 0)}/${Number(row.prestamoCuotas ?? 0)}`,
  },
  {
    // Pendientes (cuotas − pagadas) — mismo cálculo del móvil.
    key: 'cuotasPendientes', title: 'Pendientes', type: 'number', minWidth: 100,
  },
  { key: 'prestamoCuota', title: 'Cuota', type: 'currency' },
  { key: 'prestamoUtilidad', title: 'Utilidad', type: 'currency' },
  { key: 'total', title: 'Total', type: 'currency' },
  // Campos de abonos del préstamo (abono_bc.cs:2491-2504): AbonosEfectivo =
  // abonos que reducen saldo (A33), prestamoAbonos = condenas (A34),
  // AbonosTotal = efectivo + condenas (A32 — derivado en el backend).
  { key: 'abonosEfectivo', title: 'Abonos', type: 'currency' },
  { key: 'prestamoAbonos', title: 'Condenas', type: 'currency' },
  { key: 'abonosTotal', title: 'Total Abonos', type: 'currency' },
  { key: 'prestamoFechaUltimoAbono', title: 'Ultimo Abono', type: 'date' },
  { key: 'prestamoProximoCobro', title: 'Proximo Cobro', type: 'date' },
  { key: 'prestamoFechaTermina', title: 'Fecha Termina', type: 'date' },
  { key: 'cobrador', title: 'Cobrador' },
  { key: 'prestamoCreadoPor', title: 'Creado Por:' },
  {
    key: 'solicitaRenovar', title: 'Solicita Renovar?',
    // [RGM] 2026-09-17 (Rafael: "al igual que solicitar renovación (Ninguno)"):
    // combo GX "Cliente Solicita Renovar?" (prestamo.cs:6691-6695):
    // 0=(Ninguno) [GX_EmptyItemText], 1=SI, 2=NO, 3=APROBADO, 4=NO APROBADO.
    render: (row) =>
      ({ 1: 'SI', 2: 'NO', 3: 'APROBADO', 4: 'NO APROBADO' }[Number(row.solicitaRenovar)] ?? '(Ninguno)'),
  },
  { key: 'saldoPico', title: 'Saldo Pico', type: 'currency' },
  { key: 'prestamoSaldo', title: 'Saldo', type: 'currency' },
  { key: 'saldoCondenas', title: 'Saldo Condenas', type: 'currency' },
  { key: 'saldoReal', title: 'Saldo Real', type: 'currency' },
  ...(gestionAvanzada ? [{
    key: 'forzarSaldo', title: 'Forzar Saldo', minWidth: 130,
    // Forzar Saldo INLINE (prestamoww.cs:3145 — WWActionColumn con input;
    // forzarsaldo.cs:67-72 hace el UPDATE). SOLO visible en Gestión Avanzada
    // (Condicion == 0 — prestamoww.cs:3147-3153; en normal es "Invisible").
    // El clic no dispara el link de la fila.
    render: (row: any) => (
      <CTooltip content="Forzar Cambiar Saldo, Su Contabilidad será Errónea" placement="top">
        <CFormInput
          key={`fs-${row.prestamoNro}-${row.prestamoSaldo}-${saldoRev.current}`}
          type="number"
          size="sm"
          step="0.01"
          defaultValue={row.prestamoSaldo ?? ''}
          style={{ width: 120 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.stopPropagation(); e.currentTarget.blur() }
          }}
          onBlur={(e) => guardarSaldoForzado(row, e.target.value)}
        />
      </CTooltip>
    ),
  }] : []),
], [guardarSaldoForzado, saldoRev, gestionAvanzada, modFacturacion])

  // Cargar datos de los tabs del préstamo seleccionado (abonos del préstamo,
  // extracto arptextracto.cs:804-807, contrato arptcontrato.cs:723-725)
  useEffect(() => {
    if (!seleccionado) { setTabExtracto(null); setTabContrato(null); return }
    setCargandoTabs(true)
    Promise.all([
      api.get<any[]>(`/prestamos/${seleccionado.prestamoNro}/abonos?empresa=${company}`),
      api.get<any>(`/prestamos/${seleccionado.prestamoNro}/extracto?empresa=${company}`),
      api.get<any>(`/prestamos/${seleccionado.prestamoNro}/contrato?empresa=${company}`),
    ])
      .then(([, extracto, contrato]) => {
        setTabExtracto(extracto)
        setTabContrato(contrato)
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargandoTabs(false))
  }, [seleccionado, company])

  // [RGM] 2026-10-03 (pedido Rafael): ambos PDFs abren en pestaña nueva vía
  // api.abrirPdf (antes descargaban con blob + anchor).
  const descargarExtractoPdf = async () => {
    if (!seleccionado) return
    try {
      await api.abrirPdf(`/prestamos/${seleccionado.prestamoNro}/extracto.pdf?empresa=${company}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el extracto')
    }
  }

  const descargarContratoPdf = async () => {
    if (!seleccionado) return
    try {
      await api.abrirPdf(`/prestamos/${seleccionado.prestamoNro}/contrato.pdf?empresa=${company}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el contrato')
    }
  }

  const limpiarAvanzados = () => {
    setFiltroEstado(''); setFiltroRuta(''); setFiltroCobrador('')
    setFiltroTipo(''); setFiltroModalidad('')
    if (routeFilter) { setRouteFilter(''); setSearchParams({}, { replace: true }) }
  }

  // [RGM] 2026-10-02 (pedido Rafael — "Exportar .xlsx real, como el GX"):
  // exporta las filas filtradas actuales a PrestamoWWExport.xlsx (ClosedXML
  // en el backend, formato del WW GX). Los filtros activos viajan para la
  // cabecera del archivo (fila 2, pares label/valor): la barra rápida desde
  // aquí y el BUSCADOR PRINCIPAL + filtros por columna que vienen del WwGrid.
  const exportarExcel = async (filas: any[], filtrosGrilla: { label: string; valor: string }[] = []) => {
    const filtros: { label: string; valor: string }[] = []
    if (filtroEstado) filtros.push({ label: 'Estado', valor: filtroEstado })
    // Modo normal: la grilla muestra SOLO activos (prestamoww.cs:7003) — el
    // filtro efectivo es "Activos". En Gestión Avanzada sin estado → Todos.
    else if (!gestionAvanzada) filtros.push({ label: 'Estado', valor: 'Activos' })
    if (routeFilter) filtros.push({ label: 'Ruta', valor: routeFilter })
    if (filtroRuta) filtros.push({ label: 'Ruta', valor: filtroRuta })
    if (filtroCobrador) filtros.push({ label: 'Cobrador', valor: filtroCobrador })
    if (filtroTipo) filtros.push({ label: 'Tipo', valor: TIPO_LABELS[filtroTipo.toUpperCase()] ?? filtroTipo })
    if (filtroModalidad) filtros.push({ label: 'MPago', valor: METODO_PAGO_LABELS[filtroModalidad] ?? filtroModalidad })
    // Filtros internos de la grilla (buscador principal + filtros por columna).
    filtros.push(...filtrosGrilla)
    try {
      const blob = await api.postBlob('/prestamos/export', {
        empresa: company,
        prestamos: filas.map((f) => Number(f.prestamoNro)),
        filtros,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'PrestamoWWExport.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo exportar')
    }
  }

  const searchCustomers = async (text: string): Promise<any[]> => {
    const t = text.trim().toLowerCase()
    const all = await api.get<any[]>(`/clientes?empresa=${company}`)
    return all
      .filter((c) =>
        String(c.clienteCedula ?? '').includes(text.trim()) ||
        String(c.clienteNombre ?? '').toLowerCase().includes(t) ||
        String(c.clienteCelular ?? '').includes(text.trim()),
      )
      .slice(0, 10)
  }

  const renderCustomer = (c: any) => ({
    titulo: `${c.clienteNombre} — CC ${c.clienteCedula}`,
    subtitulo: `${c.zonaNombre} · ${c.clienteEstado}`,
    detalle: [c.clienteCelular, c.clienteDireccion].filter(Boolean),
  })

  const selectCustomer = (c: any) => {
    setSelectedCustomer(c)
    setForm((f) => ({ ...f, clienteCedula: String(c.clienteCedula), clienteNombre: c.clienteNombre ?? '' }))
    // Lógica GX: el Load del BC prestamo trae la información completa del cliente
    // (posición, zona, estado, reputación...) — prestamo_bc.cs:66-94.
    api
      .get<any>(`/clientes/${c.clienteCedula}?empresa=${company}`)
      .then((detail) => {
        setSelectedCustomer(detail)
        // [RGM] 2026-09-17 (reporte Rafael "error de caja"): el load del
        // préstamo GX trae la RUTA del cliente (prestamo_bc.cs:66-94) — se
        // precarga como "Ruta del prestamo" (antes quedaba el default 1 y la
        // caja de esa zona no existía → ForeignKeyNotFound: Caja).
        setForm((f) => ({ ...f, zonaCodigo: Number(detail?.zonaCodigo ?? 0) || f.zonaCodigo }))
      })
      .catch(() => {})
  }

  const clearCustomer = () => {
    setSelectedCustomer(null)
    setForm((f) => ({ ...f, clienteCedula: '', clienteNombre: '' }))
  }

  const customerSummary = (c: any) => ({
    titulo: `${c.clienteNombre} — CC ${c.clienteCedula}`,
    lineas: [
      `Ruta: ${c.zonaNombre ?? '—'}`,
      `Posición: ${c.clientePosicionRuta ?? '—'}`,
      `Estado: ${c.clienteEstado ?? '—'}`,
      `Celular: ${c.clienteCelular ?? '—'}`,
      `Direccion: ${c.clienteDireccion ?? '—'}`,
    ],
  })

  const handleEdit = (row: any) => {
    setEditing(true)
    setEditingNro(row.prestamoNro ?? null)
    setForm({
      empresaCodigo: company,
      clienteCedula: String(row.clienteCedula ?? ''),
      clienteNombre: row.clienteNombre ?? '',
      metodoPago: Number(row.prestamoMPago ?? 7),
      tipo: row.prestamoTipo ?? 'NUEVO',
      valor: row.prestamoValor ?? '',
      cuotas: row.prestamoCuotas ?? '',
      cuota: row.prestamoCuota ?? '',
      // [RGM] 2026-09-23: "Fecha Inicia Pagos" se precarga con SU campo real
      // (PrestamoFechaInicia) — antes se precargaba con prestamoFecha; y la
      // Fecha Prestamo (editable por admin) con prestamoFecha.
      // [RGM] 2026-10-01 (faltantes-multitema F): el grid trae ISO completo
      // ("2026-10-01T00:00:00") — se recorta a SOLO fecha con soloFecha()
      // (YYYY-MM-DD, mismo formato de hoyLocal()) para que el input
      // type="date" muestre la fecha sin el time con ceros.
      fechaInicia: soloFecha(row.prestamoFechaInicia ?? row.prestamoFecha, hoyLocal()),
      fechaPrestamo: soloFecha(row.prestamoFecha, hoyLocal()),
      fechaProximoCobro: soloFecha(row.prestamoProximoCobro, ''),
      zonaCodigo: Number(row.zonaCodigo ?? 0),
      prestamoDiaCobro: row.prestamoDiaCobro ?? '',
      porcentaje: 0,
      aceptaCondenas: 'No', nroAtrasosPorCondena: '', valorCondenaDiferenteA: 0, valorCondena: 0, feriados: '',
      prestamoLibreValor1: 0, prestamoLibreValor2: 0, prestamoLibreValor3: 0,
      prestamoLibreNumerico1: 0, prestamoLibreNumerico2: Number(row.solicitaRenovar ?? 0), prestamoLibreNumerico3: 0,
      prestamoLibreCaracter1: '', prestamoLibreCaracter2: '',
      prestamoObservacion: row.prestamoObservacion ?? '',
      verificado: '',
    })
    setHistory({
      prestamoAbonos: row.prestamoAbonos,
      prestamoSaldo: row.prestamoSaldo,
      // Saldo Condenas y Saldo Real del préstamo (prestamo_bc.cs:388-390:
      // Z42PrestamoSaldoReal, Z43PrestamoSaldoCondenas).
      saldoCondenas: row.saldoCondenas,
      saldoReal: row.saldoReal,
      prestamoUtilidad: row.prestamoUtilidad,
      prestamoAtrazos: row.prestamoAtrazos,
      prestamoFechaTermina: row.prestamoFechaTermina,
      prestamoFechaUltimoAbono: row.prestamoFechaUltimoAbono,
    })
    // Cliente precargado: parcial del listado + detalle del backend (misma ruta que selectCustomer).
    setSelectedCustomer({
      clienteCedula: row.clienteCedula,
      clienteNombre: row.clienteNombre ?? '',
      cobrador: row.cobrador ?? '—',
      zonaNombre: row.ruta ?? '—',
      clienteEstado: '—',
      clienteCelular: row.whatsApp ?? '—',
      clienteDireccion: row.clienteDireccion ?? '—',
      clienteObservacion: '—',
    })
    if (row.clienteCedula) {
      api
        .get<any>(`/clientes/${row.clienteCedula}?empresa=${company}`)
        .then((detail) => setSelectedCustomer((prev: any) => ({ ...detail, cobrador: detail?.cobrador ?? prev?.cobrador ?? '—' })))
        .catch(() => {})
    }
    setError('')
    setModalOpen(true)
  }

  // Alta de préstamo — réplica del BC GX prestamo_bc.cs:66-94, 350-352; 
  // el backend aplica las reglas de inserción (IsIns: prestamo_bc.cs:554-562).
  // Alta/Modificación del préstamo (réplica del BC GX prestamo_bc.cs:66-94,
  // 350-352): POST crea; PUT modifica (UPD — prestamoww DO UPDATE, con el
  // permiso "Modificar? " del cobrador validado en el backend).
  // [RGM] 2026-09-17 (reporte Rafael: "todo eso se calcula"): valores
  // CALCULADOS del préstamo — fórmulas EXACTAS del BC GX (prestamo_bc.cs)
  // replicadas en ReglasFinancieras/ReglasFechas de la réplica. Read-only.
  const mpagoP = Number(form.metodoPago) || 0
  const valorP = Number(form.valor) || 0
  const cuotasP = Number(form.cuotas) || 0
  const cuotaP = Number(form.cuota) || 0
  const esPagoUnicoP = mpagoP === 60
  // Total — prestamo_bc.cs:694-708: ≠60 → Cuotas×Cuota; ==60 → Valor+Utilidad.
  const totalCalculado = esPagoUnicoP ? valorP : cuotasP * cuotaP
  // Utilidad — prestamo_bc.cs:709-719: ≠60 y Cuotas>0 → Total − Valor.
  const utilidadCalculada = !esPagoUnicoP && cuotasP > 0 ? totalCalculado - valorP : 0
  // % — prestamo_bc.cs:728-738: ≠60 y Cuotas>0 → Utilidad×100/Valor;
  // ==60 (alta) → Cuota×100/Valor.
  const porcentajeCalculado =
    !esPagoUnicoP && cuotasP > 0 && valorP > 0
      ? Math.round((utilidadCalculada * 100) / valorP)
      : esPagoUnicoP && valorP > 0
        ? Math.round((cuotaP * 100) / valorP)
        : 0
  // Saldo inicial — prestamo_bc.cs:1825-1842: ≠60 → Total (sin abonos); ==60 → Valor.
  const saldoCalculado = esPagoUnicoP ? valorP : totalCalculado
  const computableValores = esPagoUnicoP ? valorP > 0 : cuotasP > 0 && cuotaP > 0
  // Cobrador de la RUTA seleccionada (GX: "Cobrador" del form sale de la zona
  // — prestamo.cs:881-888) con respaldo en el cobrador del cliente.
  const cobradorRutaSeleccionada = (() => {
    const r = rutasDetalle.find((x: any) => Number(x.zonaCodigo) === Number(form.zonaCodigo))
    return String(r?.cobradorNombre ?? '').trim()
  })()

  const bodyPrestamo = () => ({
    empresaCodigo: form.empresaCodigo,
    clienteCedula: Number(form.clienteCedula),
    clienteNombre: form.clienteNombre,
    metodoPago: Number(form.metodoPago),
    tipo: form.tipo,
    valor: form.valor ? Number(form.valor) : undefined,
    cuotas: Number(form.cuotas) || 0,
    cuota: form.cuota ? Number(form.cuota) : undefined,
    fechaInicia: form.fechaInicia || undefined,
    fechaPrestamo: form.fechaPrestamo || undefined,
    fechaProximoCobro: form.fechaProximoCobro || undefined,
    zonaCodigo: Number(form.zonaCodigo) || 0,
    prestamoDiaCobro: form.prestamoDiaCobro || undefined,
    porcentaje: porcentajeCalculado,
    aceptaCondenas: form.aceptaCondenas,
    nroAtrasosPorCondena: Number(form.nroAtrasosPorCondena) || 0,
    // Flag "diferente a cuota" DERIVADO (el form ya no lo pregunta — Flutter
    // cobrador): si el usuario editó el valor (≠ cuota) va 1 para que el BC
    // conserve su valor (prestamo_bc.cs:751-754); si quedó = cuota va 0.
    valorCondenaDiferenteA: form.aceptaCondenas === 'Si' && Number(form.valorCondena) !== cuotaP ? 1 : 0,
    prestamoValorCondena: Number(form.valorCondena) || 0,
    feriados: Number(form.feriados) || 0,
    prestamoLibreValor1: Number(form.prestamoLibreValor1) || 0,
    prestamoLibreValor2: Number(form.prestamoLibreValor2) || 0,
    prestamoLibreValor3: Number(form.prestamoLibreValor3) || 0,
    prestamoLibreNumerico1: Number(form.prestamoLibreNumerico1) || 0,
    prestamoLibreNumerico2: Number(form.prestamoLibreNumerico2) || 0,
    prestamoLibreNumerico3: Number(form.prestamoLibreNumerico3) || 0,
    prestamoLibreCaracter1: form.prestamoLibreCaracter1 || undefined,
    prestamoLibreCaracter2: form.prestamoLibreCaracter2 || undefined,
    prestamoObservacion: form.prestamoObservacion || undefined,
    verificado: form.verificado || undefined,
  })

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    // Validaciones del form GX web (prestamo.cs — mismos mensajes):
    if (!form.zonaCodigo) {
      setError('Seleccione Ruta')
      return
    }
    if (!form.fechaProximoCobro) {
      setError('Asignar Fecha Proximo Cobro')
      return
    }
    if (form.aceptaCondenas === 'Si' && Number(form.nroAtrasosPorCondena) <= 0) {
      setError('Asigne Atrasos por Condena')
      return
    }
    if (porcentajeCalculado < 0 || porcentajeCalculado > 999) {
      setError('Porcentaje Muy Alto Corregir Valores del Prestamo')
      return
    }
    setSaving(true)
    try {
      if (editing) {
        await api.put(`/prestamos/${editingNro}`, bodyPrestamo())
      } else {
        // Alta: la factura vinculada (cuando el alta vino de /facturas) viaja
        // OCULTA — 0 = préstamo normal, sin validación de duplicado.
        await api.post('/prestamos', { ...bodyPrestamo(), facturaNro: facturaNroCrear || 0 })
      }
      setModalOpen(false)
      setEditing(false)
      setEditingNro(null)
      // [RGM] 2026-10-05 (pedido Rafael: "cuando se crea el prestamo desde
      // facturas me lleva a prestamos; debe dejarme en facturas"): si el alta
      // vino de /facturas (crédito de la factura), al guardar VOLVEMOS a
      // Facturas — el flujo termina allá. El vínculo ya quedó guardado
      // (PrestamoFacturaNro; validacreditocreadodefactura.cs:76-88).
      if (!editing && facturaNroCrear) {
        navigate('/facturas')
        return
      }
      load()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const contact = (row: any) => {
    const phone = String(row.whatsApp ?? row.clienteCelular ?? '').trim()
    if (!phone) {
      setNotice('Cliente sin celular')
      return
    }
    window.open(`https://wa.me/${numeroWhatsApp(phone)}?text=${encodeURIComponent('Hola, te escribo sobre tu prestamo')}`, '_blank')
  }
  const pay = (row: any) => {
    navigate(`/abonos?prestamo=${row.prestamoNro}`)
  }
  const viewInvoice = (row: any) => {
    const invoiceNumber = Number(row.prestamoFacturaNro)
    if (row.prestamoFacturaNro !== undefined && row.prestamoFacturaNro !== null && invoiceNumber > 0) {
      navigate(`/facturas/${invoiceNumber}`)
      return
    }
    setNotice('El prestamo no tiene factura asociada')
  }
  const isAdmin = ['superadmin', 'companyadmin'].includes(user?.role ?? '')
  // [RGM] 2026-10-01 (faltantes-multitema C): iconos en las acciones de fila
  // (WwGrid iconoAccion — nombres del paquete @coreui/icons). FIJAS al final
  // Acciones de la fila (pedido Rafael 2026-10-01): FIJAS en icono
  // (inline-end) solo Contactar + Abonar (las operativas del día). El menú ⋮
  // lleva ÚNICAMENTE "Ver Factura", y solo cuando la empresa tiene el módulo
  // de facturación activo Y el préstamo tiene factura vinculada
  // (prestamoFacturaNro > 0). Contrato/Extracto se ven en los tabs del
  // detalle (clic en el Nro); Forzar Saldo vive como columna inline en
  // Gestión Avanzada.
  const rowActions: WwRowAction[] = [
    // Orden pedido por Rafael 2026-10-01: | Abonar | Contactar | ⋮ |
    { label: 'Abonar', icon: 'cilMoney', placement: 'inline-end', onClick: pay },
    { label: 'Contactar', icon: 'cilPhone', placement: 'inline-end', onClick: contact },
    {
      label: 'Ver Factura',
      icon: 'cilSearch',
      onClick: viewInvoice,
      mostrar: (fila) => modFacturacion && Number(fila.prestamoFacturaNro ?? 0) > 0,
    },
  ]

  const card = (icon: any, title: string, content: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className=" d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
        <CIcon icon={icon} size="sm" /> {title}
      </CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{content}</CRow>
      </CCardBody>
    </CCard>
  )

  const ro = (value: React.ReactNode) => (
    <div
      className="form-control bg-body-tertiary text-body-secondary"
      style={{ minHeight: 'calc(1.5em + 0.75rem + 2px)', display: 'flex', alignItems: 'center' }}
    >
      {value ?? '—'}
    </div>
  )

  const [totalReg, setTotalReg] = useState(visibleRows.length)
  // [RGM] 2026-10-07 (B+C): modo del motor (Solo Facturación → título Créditos).
  const [modoCtv, setModoCtv] = useState('PRESTAMOS')
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4 className="mb-0">{modoCtv === 'FACTURACION' ? 'Créditos' : 'Prestamos'}</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
        {/* [RGM] 2026-09-17 (Rafael: "déjalo por fuera como GPS Cobradores en
            Rutas"): el modo Gestión Avanzada vive en el ENCABEZADO de la
            página (fuera de la grilla) — muestra TODOS los préstamos (el WW
            normal filtra solo activos — prestamoww.cs:7003). */}
        <CCol xs="auto" className="text-end align-self-center">
          {/* [RGM] 2026-09-17 (Rafael): mismo tamaño e ícono que el botón
              "GPS Cobradores" de Rutas. */}
          <CButton
            color={gestionAvanzada ? 'primary' : 'outline-primary'}
            onClick={() => setGestionAvanzada((v) => !v)}
          >
            <CIcon icon={cilSettings} className="me-2" />
            Gestión Avanzada
          </CButton>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {notice && <CAlert color="warning" dismissible onClose={() => setNotice('')}>{notice}</CAlert>}

      {/* ── Préstamo seleccionado: colapso + tabs (patrón Empresas superadmin) ── */}
      {seleccionado && (
        <CCard className="shadow-sm mb-3">
          <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <div className="fs-5 fw-bold">
                {seleccionado.prestamoNro} · {seleccionado.clienteNombre}
              </div>
              <div className="text-body-secondary small mt-1">
                <EstadoPrestamoBadge estado={seleccionado.estadoCalc} />{' '}
                Ruta: {seleccionado.ruta?.trim() || '—'} · Saldo: {fmtMoneda(seleccionado.prestamoSaldo)}
              </div>
            </div>
            <CButton size="sm" color="outline-secondary" onClick={() => setSeleccionado(null)}>
              ← Volver al listado
            </CButton>
          </CCardBody>
        </CCard>
      )}
      {seleccionado && (
        <CTabs defaultActiveItemKey="abonos" className="mb-3">
          <CTabList variant="tabs">
            <CTab itemKey="abonos">Abonos</CTab>
            <CTab itemKey="atrasos">Atrasos</CTab>
            <CTab itemKey="extracto">Extracto</CTab>
            <CTab itemKey="contrato">Contrato</CTab>
          </CTabList>
          <CTabContent>
            <CTabPanel className="pt-3" itemKey="abonos">
              {cargandoTabs ? <div className="text-center py-4"><CSpinner color="primary" /></div>
                : !tabExtracto?.abonos?.length ? <div className="text-body-secondary py-3 text-center">Sin abonos</div>
                : <WwGrid title="Abonos" columns={columnasTabAbonos} rows={tabExtracto.abonos} onTotalChange={() => undefined} />}
            </CTabPanel>
            <CTabPanel className="pt-3" itemKey="atrasos">
              {cargandoTabs ? <div className="text-center py-4"><CSpinner color="primary" /></div>
                : !tabExtracto?.atrasos?.length ? <div className="text-body-secondary py-3 text-center">Sin atrasos</div>
                : <WwGrid title="Atrasos" columns={columnasTabAtrasos} rows={tabExtracto.atrasos} onTotalChange={() => undefined} />}
            </CTabPanel>
            <CTabPanel className="pt-3" itemKey="extracto">
              {cargandoTabs ? <div className="text-center py-4"><CSpinner color="primary" /></div>
                : !tabExtracto?.prestamo ? <div className="text-body-secondary py-3 text-center">Sin datos</div>
                : <VistaExtracto prestamo={tabExtracto.prestamo} abonos={tabExtracto.abonos ?? []}
                    descargar={descargarExtractoPdf} />}
            </CTabPanel>
            <CTabPanel className="pt-3" itemKey="contrato">
              {cargandoTabs ? <div className="text-center py-4"><CSpinner color="primary" /></div>
                : !tabContrato?.contrato ? <div className="text-body-secondary py-3 text-center">Sin datos</div>
                : <VistaContrato contrato={tabContrato.contrato} plan={tabContrato.planPagos ?? []}
                    totalPagar={tabContrato.totalPagar ?? 0} cuotasPagas={tabContrato.cuotasPagas ?? 0}
                    firmaClienteUrl={tabContrato.clienteFirmaUrl} firmaClienteBase64={tabContrato.clienteFirmaBase64}
                    firmaCodeudorUrl={tabContrato.clienteFirmaCodeudorUrl} firmaCodeudorBase64={tabContrato.clienteFirmaCodeudorBase64}
                    descargar={descargarContratoPdf} />}
            </CTabPanel>
          </CTabContent>
        </CTabs>
      )}

      {!seleccionado && (
        <WwGrid
          filters={
            <div className="d-flex flex-column gap-2">
      {/* ── Barra de filtros rápidos (wireframe aprobado — filtros del WW GX
             prestamoww.cs:230 simplificados: búsqueda, estado, ruta, cobrador,
             tipo, modalidad) ── */}
      <div className="d-flex flex-column gap-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {/* Botones de estado: Activos (modo normal, prestamoww.cs:7003
              solo saldo > 0) | Todos (gestión avanzada) | estados */}
          <div className="d-flex gap-1 flex-wrap">
            <CButton
              size="sm"
              color={!gestionAvanzada && filtroEstado === '' ? 'primary' : 'outline-primary'}
              onClick={() => { setGestionAvanzada(false); setFiltroEstado('') }}
            >
              Activos
            </CButton>
            <CButton
              size="sm"
              color={gestionAvanzada && filtroEstado === '' ? 'primary' : 'outline-primary'}
              onClick={() => { setGestionAvanzada(true); setFiltroEstado('') }}
            >
              Todos
            </CButton>
            {estadosDominio.map((est) => (
              <CButton
                key={est}
                size="sm"
                color={filtroEstado === est ? 'primary' : 'outline-primary'}
                onClick={() => setFiltroEstado(filtroEstado === est ? '' : est)}
              >
                {est}
              </CButton>
            ))}
          </div>
          {/* Selectores avanzados: MISMA línea que los botones, alineados a la derecha */}
          <div className="d-flex flex-wrap align-items-center gap-2 ms-auto justify-content-end">
            <CFormSelect
              className="w-auto"
              value={filtroRuta}
              onChange={(e) => setFiltroRuta(e.target.value)}
            >
              <option value="">Todas las Rutas</option>
              {rutas.map((r) => <option key={r} value={r}>{r}</option>)}
            </CFormSelect>
            <CFormSelect
              className="w-auto"
              value={filtroCobrador}
              onChange={(e) => setFiltroCobrador(e.target.value)}
            >
              <option value="">Todos los Cobradores</option>
              {cobradores.map((cb) => <option key={cb} value={cb}>{cb}</option>)}
            </CFormSelect>
            <CFormSelect
              className="w-auto"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos los Tipos</option>
              {Object.entries(TIPO_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </CFormSelect>
            <CFormSelect
              className="w-auto"
              value={filtroModalidad}
              onChange={(e) => setFiltroModalidad(e.target.value)}
            >
              <option value="">Todas las Modalidades</option>
              {Object.entries(METODO_PAGO_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </CFormSelect>
          </div>
        </div>

        {/* Chips de filtros activos + Limpiar */}
        {filtrosActivos.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="d-flex flex-wrap gap-1">
            {filtrosActivos.map((f) => (
              <CBadge
                key={f.clave}
                color="secondary"
                className="d-inline-flex align-items-center gap-1 fw-normal"
              >
                {f.label}
                <CIcon
                  icon={cilX}
                  size="sm"
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (f.clave === 'ruta-param') { setRouteFilter(''); setSearchParams({}, { replace: true }) }
                    if (f.clave === 'estado') setFiltroEstado('')
                    if (f.clave === 'ruta') setFiltroRuta('')
                    if (f.clave === 'cobrador') setFiltroCobrador('')
                    if (f.clave === 'tipo') setFiltroTipo('')
                    if (f.clave === 'modalidad') setFiltroModalidad('')
                  }}
                />
              </CBadge>
            ))}
            </div>
            <CButton size="sm" color="outline-secondary" onClick={limpiarAvanzados}>
              Limpiar filtros
            </CButton>
          </div>
        )}
      </div>
            </div>
          }
          title="Prestamo"
          columns={columns}
          rows={visibleRows}
          onExportar={exportarExcel}
          onTotalChange={setTotalReg}
          loading={loading}
          busquedaPlaceholder="Buscar por cliente (cédula, nombre, dirección o celular)..." 
          onRowClick={(row) => setSeleccionado(row)}
          onAgregar={openNew}
          onEditar={handleEdit}
          // Eliminar existe en el grid actions del WW GX (GX_BtnDelete —
          // prestamoww.cs:3142 "fa fa-times"). [RGM] 2026-10-02: cableado real
          // (el DELETE existe y restaura la caja — PrestamosController.Eliminar).
          onEliminar={(row) => {
            if (!window.confirm(`¿Eliminar el prestamo ${row.prestamoNro}?`)) return
            api.delete(`/prestamos/${row.prestamoNro}?empresa=${company}`)
              .then(() => {
                setNotice('Prestamo eliminado.')
                load()
              })
              .catch((e: any) => setError(e.message ?? 'Error al eliminar'))
          }}
          rowActions={rowActions}
          onRowLink={(colKey, row) => {
            if (colKey === 'prestamoNro') navigate(`/prestamos/${row.prestamoNro}`)
          }}
        />
      )}

      <CModal visible={modalOpen} onClose={() => setModalOpen(false)} size="lg" className="ww-modal-prestamo">
        <CModalHeader>
          <CModalTitle>{editing ? 'Editar Prestamo' : 'Nuevo Prestamo'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {error && <CAlert color="danger">{error}</CAlert>}
          <CForm ref={formRef} onSubmit={create}>
            {card(cilFile, 'Informacion General', (
              <>
                <CCol md={6}>
                  <CFormLabel>Prestamo Nro</CFormLabel>
                  {ro(editing ? editingNro : 'Se genera automaticamente')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Fecha Prestamo</CFormLabel>
                  {/* [RGM] 2026-09-23 (pedido Rafael): "Fecha Prestamo" editable
                      para el administrador (WW GX: edtPrestamoFecha —
                      prestamo.cs:6359 edtPrestamoFecha_Enabled = 1).
                      [RGM] 2026-10-02 (reporte Rafael): el admin la edita en
                      ALTA y EDICIÓN; los demás roles la ven de solo lectura
                      (en ALTA todavía no existe → "Automatica (hoy)" y la pone
                      el sistema). El backend ya ignora el cambio para roles
                      2/4 (PrestamosController.cs:404, esAdminFecha). */}
                  {isAdmin
                    ? <CFormInput type="date" value={form.fechaPrestamo} onChange={(e) => setForm({ ...form, fechaPrestamo: e.target.value })} />
                    : ro(editing ? form.fechaPrestamo : 'Automatica (hoy)')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Empresa</CFormLabel>
                  {ro(form.empresaCodigo)}
                </CCol>
              </>
            ))}

            {card(cilUser, 'Cliente', (
              <>
                <CCol md={12}>
                  <WwSearchField
                    placeholder="Cedula, nombre o celular"
                    onBuscar={searchCustomers}
                    renderItem={renderCustomer}
                    onSeleccionar={selectCustomer}
                    limpiar
                    onLimpiar={clearCustomer}
                    seleccionado={selectedCustomer !== null}
                    resumen={selectedCustomer ? customerSummary(selectedCustomer) : undefined}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cedula / NUI / ID</CFormLabel>
                  {ro(selectedCustomer?.clienteCedula)}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>WhatsApp</CFormLabel>
                  {ro(selectedCustomer?.clienteCelular)}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Ruta principal del cliente</CFormLabel>
                  {ro(selectedCustomer?.zonaNombre)}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cobrador</CFormLabel>
                  {ro(cobradorRutaSeleccionada || selectedCustomer?.cobrador || '—')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Ruta del prestamo</CFormLabel>
                  {/* [RGM] 2026-09-17 (reporte Rafael "error de caja"): combo
                      "Seleccione Ruta" del GX (panel SD) — las rutas de la
                      empresa; al elegir el cliente se precarga SU ruta.
                      [RGM] 2026-10-02 (reporte Rafael, GX prestamo.cs:5415 y
                      :6240 — edtZonaCodigo_Enabled = 0): la Ruta del préstamo
                      viene del cliente y NO es editable; el combo queda
                      deshabilitado mostrando la ruta precargada (alta y
                      edición). */}
                  <CFormSelect
                    disabled
                    value={String(form.zonaCodigo || '')}
                    onChange={(e) => setForm({ ...form, zonaCodigo: Number(e.target.value) })}
                  >
                    <option value="">Seleccione Ruta</option>
                    {rutasDetalle.map((r: any) => (
                      <option key={r.zonaCodigo} value={r.zonaCodigo}>
                        {String(r.zonaNombre ?? '').trim()}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Cliente Estado</CFormLabel>
                  {ro(selectedCustomer?.clienteEstado)}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Observa</CFormLabel>
                  {ro(selectedCustomer?.clienteObservacion)}
                </CCol>
              </>
            ))}

            {card(cilSettings, 'Configuracion', (
              <>
                <CCol md={4}>
                  <CFormLabel>Tipo</CFormLabel>
                  <CFormSelect value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                    <option value="NUEVO">Nuevo</option>
                    <option value="Renovacion">Renovacion</option>
                  </CFormSelect>
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Modalidad de pago</CFormLabel>
                  <CFormSelect value={form.metodoPago} onChange={(e) => {
                    const mp = Number(e.target.value)
                    // [RGM] 2026-10-06 (pedido Rafael): al pasar a Pago Libre
                    // (60) el campo Cuotas se OCULTA — se resetea para no
                    // enviar un valor viejo (el backend exige Cuotas=Cero en
                    // Libre y el form quedaría trabado sin poder corregirlo).
                    setForm({ ...form, metodoPago: mp, ...(mp === 60 ? { cuotas: '' } : {}) })
                  }}>
                    <option value={1}>Diario</option>
                    <option value={7}>Semanal</option>
                    <option value={15}>Quincenal</option>
                    <option value={30}>Mensual</option>
                    <option value={60}>Pago Libre</option>
                  </CFormSelect>
                </CCol>
                <CCol md={4}>
                  {/* Dia Cobro: combobox réplica del GX (prestamoww.cs:4585-4595 —
                      Lu..Do, 15, 30, QT=15-30, 52=5-20, 10=DECADAL; lista fija del
                      grid en prestamoww.cs:5458). NO es un input libre. */}
                  <CFormLabel>Dia Cobro</CFormLabel>
                  <CFormSelect value={form.prestamoDiaCobro} onChange={(e) => setForm({ ...form, prestamoDiaCobro: e.target.value })}>
                    {/* GX_EmptyItemText del combo GX = "(Ninguno)" (prestamo.cs:6653). */}
                    <option value="">(Ninguno)</option>
                    <option value="Lu">Lu</option>
                    <option value="Ma">Ma</option>
                    <option value="Mi">Mi</option>
                    <option value="Ju">Ju</option>
                    <option value="Vi">Vi</option>
                    <option value="Sa">Sa</option>
                    <option value="Do">Do</option>
                    <option value="15">15</option>
                    <option value="30">30</option>
                    <option value="QT">15-30</option>
                    <option value="52">5-20</option>
                    <option value="38">3-18</option>
                    <option value="10">DECADAL</option>
                  </CFormSelect>
                </CCol>
              </>
            ))}

            {card(cilDollar, 'Valores', (
              <>
                <CCol md={6}>
                  <CFormLabel>Valor Prestamo</CFormLabel>
                  {/* [RGM] 2026-10-02 (pedido Rafael — muestra en Préstamo): el
                      símbolo de la moneda configurada como prefijo persistente
                      (mismo patrón del buscador WwSearchField) — orienta al
                      digitar montos sin settar el valor. */}
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={form.valor}
                      onValueChange={(v) => setForm({ ...form, valor: v.value })}
                      thousandSeparator=","
                      decimalSeparator="."
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      inputMode="decimal"
                    />
                  </CInputGroup>
                </CCol>
                {/* [RGM] 2026-10-06 (pedido Rafael: préstamo Libre — modalidad
                    primero, sin campo Cuotas, cuotas 0 permitidas): en Pago
                    Libre (MPago=60) el campo Cuotas se OCULTA — el BC exige
                    Cuotas=0 (prestamo_bc.cs:987-989); el payload envía 0
                    (campo vacío en alta / 0 precargado en edición). */}
                {!esPagoUnicoP && (
                  <CCol md={6}>
                    <CFormLabel>Cuotas</CFormLabel>
                    <CFormInput type="number" value={form.cuotas} onChange={(e) => setForm({ ...form, cuotas: e.target.value })} onBlur={(e) => setForm({ ...form, cuotas: normalizarNumeroTexto(e.target.value) })} />
                  </CCol>
                )}
                <CCol md={6}>
                  <CFormLabel>Cuota</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={form.cuota}
                      onValueChange={(v) => setForm({ ...form, cuota: v.value })}
                      thousandSeparator=","
                      decimalSeparator="."
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      inputMode="decimal"
                    />
                  </CInputGroup>
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Porcentaje</CFormLabel>
                  {/* [RGM] 2026-09-17 (pedido Rafael): el % NO es editable —
                      se CALCULA con la fórmula del BC GX (prestamo_bc.cs:728-738):
                      Utilidad×100/Valor. Label GX "Porcentaje" (prestamo.cs:1112)
                      y control deshabilitado (prestamo.cs:2823). */}
                  {ro(computableValores || (esPagoUnicoP && valorP > 0) ? String(porcentajeCalculado) : 'Se calcula automaticamente')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Utilidad</CFormLabel>
                  {ro(computableValores ? fmtMoneda(utilidadCalculada) : 'Se calcula automaticamente')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Total</CFormLabel>
                  {ro(computableValores ? fmtMoneda(totalCalculado) : 'Se calcula automaticamente')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Saldo</CFormLabel>
                  {ro(computableValores ? fmtMoneda(saldoCalculado) : 'Se calcula automaticamente')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Acepta Condenas? (Intereses x mora)</CFormLabel>
                  <CFormSelect value={form.aceptaCondenas} onChange={(e) => {
                    const v = e.target.value
                    // Flutter cobrador (nuevo_prestamo_screen.dart:456-461):
                    // al activar condenas, "Valor Condena" = valor de la CUOTA
                    // (editable después) — prestamo_bc.cs:751-754.
                    setForm({ ...form, aceptaCondenas: v, ...(v === 'Si' ? { valorCondena: Number(form.cuota) || 0 } : {}) })
                  }}>
                    <option value="No">No</option>
                    <option value="Si">Si</option>
                  </CFormSelect>
                </CCol>
                {/* [RGM] 2026-09-17 (pedido Rafael, validado contra el Flutter
                    del cobrador — nuevo_prestamo_screen.dart:449-480): los
                    campos de condena se OCULTAN al inicio y aparecen al activar
                    "Acepta Condenas"; al activarlo, "Valor Condena" se precarga
                    con el valor de la CUOTA y queda EDITABLE (prestamo_bc.cs:
                    751-754: sin valor diferente, ValorCondena = Cuota). Sin
                    campo "diferente a cuota". FERIADOS siempre visible. */}
                {form.aceptaCondenas === 'Si' && (
                  <>
                    <CCol md={6}>
                      <CFormLabel>Valor Condena</CFormLabel>
                      <CInputGroup>
                        <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                        <NumericFormat
                          className="form-control"
                          value={form.valorCondena}
                          onValueChange={(v) => setForm({ ...form, valorCondena: v.floatValue ?? 0 })}
                          thousandSeparator=","
                          decimalSeparator="."
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          inputMode="decimal"
                        />
                      </CInputGroup>
                    </CCol>
                    <CCol md={6}>
                      <CFormLabel>Nro Atrasos por Condena</CFormLabel>
                      <CFormInput
                        type="number"
                        value={form.nroAtrasosPorCondena}
                        onChange={(e) => setForm({ ...form, nroAtrasosPorCondena: e.target.value })}
                        onBlur={(e) => setForm({ ...form, nroAtrasosPorCondena: normalizarNumeroTexto(e.target.value) })}
                      />
                    </CCol>
                  </>
                )}
                {/* [RGM] 2026-10-02 (pedido Rafael): si la empresa TRABAJA
                    FESTIVOS el campo Feriados se OCULTA (el cálculo no suma
                    feriados — ReglasFechas con el flag de la empresa). */}
                {!trabajaFestivo && (
                  <CCol md={6}>
                    <CFormLabel>Feriados</CFormLabel>
                    <CFormInput type="number" value={form.feriados} onChange={(e) => setForm({ ...form, feriados: e.target.value })} onBlur={(e) => setForm({ ...form, feriados: normalizarNumeroTexto(e.target.value) })} />
                  </CCol>
                )}
                <CCol md={6}>
                  {/* GX: combo "Cliente Solicita Renovar?" (prestamo.cs:6691-6695,
                      label 1402): 0=(Ninguno), 1=SI, 2=NO, 3=APROBADO, 4=NO APROBADO. */}
                  <CFormLabel>Cliente Solicita Renovar?</CFormLabel>
                  <CFormSelect
                    value={form.prestamoLibreNumerico2}
                    onChange={(e) => setForm({ ...form, prestamoLibreNumerico2: Number(e.target.value) })}
                  >
                    <option value={0}>(Ninguno)</option>
                    <option value={1}>SI</option>
                    <option value={2}>NO</option>
                    <option value={3}>APROBADO</option>
                    <option value={4}>NO APROBADO</option>
                  </CFormSelect>
                </CCol>
              </>
            ))}

            {card(cilCalendar, 'Fechas', (
              <>
                {/* [RGM] 2026-10-01 (faltantes-multitema F): el campo "Fecha
                    Prestamo" duplicado de esta tarjeta se QUITÓ — queda SOLO
                    el de la tarjeta "Informacion General" (editable por admin
                    en edición — prestamo.cs:6359 edtPrestamoFecha_Enabled = 1). */}
                <CCol md={12}>
                  <CFormLabel>Proximo Cobro</CFormLabel>
                  <CFormInput type="date" value={form.fechaProximoCobro} onChange={(e) => setForm({ ...form, fechaProximoCobro: e.target.value })} />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Inicia Pagos</CFormLabel>
                  {/* GX: al grabar el BC deriva PrestamoFechaInicia =
                      PrestamoProximoCobro (prestamo_bc.cs:759/1036) — en el
                      ALTA se muestra el mismo valor; en edición se conserva
                      el campo editable. */}
                  {editing ? (
                    <CFormInput type="date" value={form.fechaInicia} onChange={(e) => setForm({ ...form, fechaInicia: e.target.value })} />
                  ) : (
                    ro(form.fechaProximoCobro || '—')
                  )}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Termina Pagos</CFormLabel>
                  {ro(editing ? (history?.prestamoFechaTermina ?? '—') : 'Se calcula al crear')}
                </CCol>
              </>
            ))}

            {card(cilChart, 'Abonos y Saldos', (
              <>
                <CCol md={6}>
                  <CFormLabel>Abonos a Prestamo</CFormLabel>
                  {ro(fmtMoneda(editing ? history?.prestamoAbonos : 0))}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Abonos a Condenas</CFormLabel>
                  {ro('—')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Saldo Prestamo</CFormLabel>
                  {/* [RGM] 2026-09-17 (reporte Rafael: "saldo... todo eso se
                      calcula"): en ALTA se muestran los valores calculados. */}
                  {ro(editing ? fmtMoneda(history?.prestamoSaldo) : fmtMoneda(computableValores ? saldoCalculado : undefined))}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Saldo Condenas</CFormLabel>
                  {ro(editing ? fmtMoneda(history?.saldoCondenas) : fmtMoneda(0))}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Saldo Total</CFormLabel>
                  {ro(editing ? fmtMoneda(history?.saldoReal) : fmtMoneda(computableValores ? saldoCalculado : undefined))}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Utilidad Actual</CFormLabel>
                  {ro(editing ? fmtMoneda(history?.prestamoUtilidad) : fmtMoneda(computableValores ? utilidadCalculada : undefined))}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Atraso</CFormLabel>
                  {ro(editing ? (history?.prestamoAtrazos ?? '—') : 0)}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Utilidad Real</CFormLabel>
                  {ro('—')}
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Ultima Visita</CFormLabel>
                  {ro(history?.prestamoFechaUltimoAbono ?? '—')}
                </CCol>
              </>
            ))}

            {card(cilPencil, 'Observacion', (
              <>
                <CCol md={12}>
                  <CFormLabel>Observacion</CFormLabel>
                  <CFormInput value={form.prestamoObservacion} onChange={(e) => setForm({ ...form, prestamoObservacion: e.target.value })} onBlur={(e) => setForm({ ...form, prestamoObservacion: capitalizarPalabras(e.target.value) })} />
                </CCol>
                <CCol md={12}>
                  <CFormLabel>Verificado</CFormLabel>
                  <CFormInput value={form.verificado} onChange={(e) => setForm({ ...form, verificado: e.target.value })} onBlur={(e) => setForm({ ...form, verificado: capitalizarPalabras(e.target.value) })} />
                </CCol>
              </>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={saving} onClick={() => formRef.current?.requestSubmit()}>
            {saving ? 'Guardando…' : 'Guardar'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

/// Contrato del préstamo como DOCUMENTO (réplica visual de arptcontrato.cs):
/// Datos Deudor / Codeudor / Crédito + textos legales + PLAN DE PAGOS + firmas.
function VistaContrato({ contrato, plan, totalPagar, cuotasPagas = 0, descargar,
  firmaClienteUrl, firmaClienteBase64, firmaCodeudorUrl, firmaCodeudorBase64 }: any) {
  const fila = (label: string, valor?: any) => (
    <div className="d-flex gap-2" style={{ fontSize: 13 }}>
      <span style={{ minWidth: 130, fontWeight: 600 }}>{label}</span>
      <span>{String(valor ?? '')}</span>
    </div>
  )
  const fecha = (iso?: string) => (iso ? String(iso).slice(0, 10) : '')

  return (
    <CCard className="border-0 shadow-sm" style={{ overflow: 'hidden' }}>
      {/* Encabezado sobrio UNIFICADO (mismo patrón de Informes): sin franja
          azul — el título solo, los botones van en el body. */}
      <CCardHeader className="fw-semibold text-uppercase">CONTRATO DE CREDITO Nro. {contrato.prestamoNro}</CCardHeader>
      <CCardBody>
        <div className="d-flex justify-content-end">
          <CButton color="primary" variant="outline" onClick={descargar}>
            <CIcon icon={cilPrint} className="me-1" /> Descargar PDF
          </CButton>
        </div>

        {/* Totales del contrato (arptcontrato.cs:263-265 "Total a Pagar:").
            Cuotas Abonadas (arptextracto.cs:287) = AbonosEfectivo ÷ Cuota
            (arptextracto.cs:193-200); Cuotas Pendientes (arptextracto.cs:276)
            = Cuotas − Abonadas (arptextracto.cs:201). */}
        <div className="info-resumen-grid mt-3 mb-3">
          <ResumenCardPrestamo clase="info-creditos" etiqueta="Total a Pagar" valor={fmtMoneda(totalPagar)} />
          <ResumenCardPrestamo clase="info-abonos" etiqueta="Cuotas Abonadas" valor={String(cuotasPagas ?? 0)} />
          <ResumenCardPrestamo clase="info-atrasos" etiqueta="Cuotas Pendientes"
            valor={String(Math.max(Number(plan?.length ?? 0) - Number(cuotasPagas ?? 0), 0))} />
        </div>

        <div className="text-center fw-bold mb-2">{String(contrato.empresaNombre ?? '').trim()}</div>

        {/* Mismo formato de la FACTURA [RGM] 2026-10-05 (pedido Rafael:
            tarjeta azul del deudor/codeudor + tarjeta azul del crédito). */}
        <div className="row g-3 mt-3">
          <div className="col-md-6">
            <div className="factura-panel h-100">
              <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS DEL DEUDOR</h6>
              <div className="row g-1">
                <div className="col-6">
                  {fila('Cedula Nro.:', contrato.clienteCedula)}
                  {fila('Nombre:', contrato.clienteNombre)}
                  {fila('Direccion:', contrato.clienteDireccion)}
                </div>
                <div className="col-6">
                  {fila('Celular:', contrato.clienteCelular)}
                  {fila('Telefono:', contrato.clienteTelefono)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="factura-panel h-100">
              <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS DEL CODEUDOR</h6>
              <div className="row g-1">
                <div className="col-6">
                  {fila('Cedula Nro.:', contrato.clienteCodeudorCedula)}
                  {fila('Nombre:', contrato.clienteCodeudorNombre)}
                  {fila('Direccion:', contrato.clienteCodeudorDireccion)}
                </div>
                <div className="col-6">
                  {fila('Celular:', contrato.clienteCodeudorCelular)}
                  {fila('Telefono:', contrato.clienteCodeudorTelefono)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="factura-panel mt-3">
          <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS DEL CREDITO</h6>
          <div className="row g-1">
            <div className="col-6">
              {fila('Fecha:', fecha(contrato.prestamoFecha))}
              {fila('Credito Nro.:', contrato.prestamoNro)}
              {fila('Valor:', fmtMoneda(contrato.prestamoValor))}
              {fila('Nro. de Cuotas:', contrato.prestamoCuotas)}
              {fila('Valor Cuota:', fmtMoneda(contrato.prestamoCuota))}
            </div>
            <div className="col-6">
              {fila('Total a Pagar:', fmtMoneda(totalPagar))}
              {fila('Metodo de Pago:', contrato.prestamoMPago)}
              {fila('Dia de Cobro:', contrato.prestamoDiaCobro)}
              {fila('Cliente Negocio:', contrato.clienteNegocio)}
            </div>
          </div>
        </div>

        {/* [RGM] 2026-09-16 (reporte Rafael): fondo del TEMA — el gris fijo
            #f8f9fa en dark dejaba letra blanca sobre fondo claro (invisible). */}
        <div className="mt-3 p-2 border rounded" style={{ fontSize: 12, background: 'var(--cui-tertiary-bg)' }}>
          <strong>Nota Legal:</strong> Este contrato implica todas las cuestiones legales de las
          obligaciones contraidas por el Deudor y el Codeudor, para el cumplimiento del mismo.
          <br />
          <strong>Penalidades:</strong> El valor de la cuota acordada debe ser pagada en las fechas
          correspondientes y su valor completo, de lo contrario se generara un atraso, se le CONDENA
          con el valor Acordado, cada que se cumpla esta condicion.
        </div>

        <h6 className="fw-bold text-primary mt-3 mb-2">PLAN DE PAGOS</h6>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm table-bordered factura-tabla mb-0" style={{ fontSize: 12 }}>
            <thead>
              <tr><th>Nro. cuota</th><th>Fecha Pago</th><th className="text-end">Valor Cuota</th><th className="text-end">Saldo</th></tr>
            </thead>
            <tbody>
              {plan.map((p: any) => {
                const nro = Number(p.nro ?? 0)
                // Estado de la cuota (igual que el móvil): pagada → fila verde
                // con badge 'Pagado'; atrasada (fecha vencida sin pagar) →
                // naranja con badge 'Atrasada'; pendiente → normal.
                const pagada = nro <= Number(cuotasPagas ?? 0)
                const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
                const fPago = p.fechaPago ? new Date(String(p.fechaPago).slice(0, 10) + 'T00:00:00') : null
                const atrasada = !pagada && fPago && fPago.getTime() < hoy.getTime()
                const estilo = pagada
                  // [RGM] 2026-09-16: tintes translúcidos — legibles en light y dark.
                  ? { background: 'rgba(30, 158, 106, 0.18)', borderLeft: '5px solid #1E9E6A' }
                  : atrasada ? { background: 'rgba(245, 158, 11, 0.18)', borderLeft: '5px solid #F59E0B' } : undefined
                return (
                  <tr key={p.nro} style={estilo}>
                    <td>{p.nro}</td>
                    <td>{fecha(p.fechaPago)}</td>
                    <td className="text-end">{fmtMoneda(p.valorCuota)}</td>
                    <td className="text-end">{fmtMoneda(p.saldo)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="text-secondary mt-1" style={{ fontSize: 11 }}>* No se Distingue los Domingos y Festivos</div>

        <div className="row mt-4 pt-3" style={{ borderTop: '1px dashed #ccc' }}>
          {/* Firmas del contrato — réplica de arptcontrato.cs:266-267 (deudor)
              y 283-284 (codeudor): el GX dibuja el bitmap de la firma SOBRE la
              línea; sin firma cargada se muestra solo la línea + label.
              Dos orígenes: URL pública R2 (web) o blob base64 (Flutter —
              grabarfirmacliente.cs). */}
          <div className="col-6 text-center">
            {(firmaClienteUrl || firmaClienteBase64) && (
              <img
                src={firmaClienteUrl ?? `data:image/png;base64,${firmaClienteBase64}`}
                alt="Firma del deudor"
                style={{ maxHeight: 90, maxWidth: '100%', marginBottom: 4 }}
              />
            )}
            <div style={{ borderTop: '1px solid #000', paddingTop: 6, fontSize: 12 }}>
              FIRMA DEUDOR
            </div>
          </div>
          <div className="col-6 text-center">
            {(firmaCodeudorUrl || firmaCodeudorBase64) && (
              <img
                src={firmaCodeudorUrl ?? `data:image/png;base64,${firmaCodeudorBase64}`}
                alt="Firma del codeudor"
                style={{ maxHeight: 90, maxWidth: '100%', marginBottom: 4 }}
              />
            )}
            <div style={{ borderTop: '1px solid #000', paddingTop: 6, fontSize: 12 }}>
              FIRMA CODEUDOR
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  )
}

/// EXTRACTO del préstamo (réplica visual de arptextracto.cs): tarjetas con los
/// datos del crédito, el estado (abonado/saldo) y el historial de abonos.
function VistaExtracto({ prestamo, abonos, descargar }: any) {
  const fila = (label: string, valor?: any) => (
    <div className="d-flex gap-2" style={{ fontSize: 13 }}>
      <span style={{ minWidth: 130, fontWeight: 600 }}>{label}</span>
      <span>{String(valor ?? '')}</span>
    </div>
  )
  const fecha = (iso?: string) => (iso ? String(iso).slice(0, 10) : '')

  return (
    <CCard className="border-0 shadow-sm" style={{ overflow: 'hidden' }}>
      {/* Encabezado sobrio UNIFICADO (mismo patrón de Informes): sin franja
          azul — el título solo, los botones van en el body. */}
      <CCardHeader className="fw-semibold text-uppercase">EXTRACTO — Prestamo Nro. {prestamo.prestamoNro}</CCardHeader>
      <CCardBody>
        <div className="d-flex justify-content-end">
          <CButton color="primary" variant="outline" onClick={descargar}>
            <CIcon icon={cilPrint} className="me-1" /> Descargar PDF
          </CButton>
        </div>
        {/* Mismo formato de la FACTURA [RGM] 2026-10-05 (pedido Rafael:
            "tarjeta azul" del cliente + la del crédito). */}
        <div className="row g-3 mt-3">
          <div className="col-md-6">
            <div className="factura-panel h-100">
              <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS DEL CLIENTE</h6>
              <div className="row g-1">
                <div className="col-6">
                  {fila('Nombre:', prestamo.clienteNombre)}
                  {fila('Cedula:', prestamo.clienteCedula)}
                  {fila('Telefono:', prestamo.clienteTelefono)}
                </div>
                <div className="col-6">
                  {fila('Celular:', prestamo.clienteCelular)}
                  {fila('Direccion:', prestamo.clienteDireccion)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="factura-panel h-100">
              <h6 className="fw-bold text-primary mb-2" style={{ fontSize: 13 }}>DATOS DEL CREDITO</h6>
              <div className="row g-1">
                <div className="col-6">
                  {fila('Tipo:', prestamo.prestamoTipo)}
                  {fila('Fecha:', fecha(prestamo.prestamoFecha))}
                  {fila('Valor:', fmtMoneda(prestamo.prestamoValor))}
                  {fila('Cuota:', fmtMoneda(prestamo.prestamoCuota))}
                </div>
                <div className="col-6">
                  {fila('Cuotas:', prestamo.prestamoCuotas)}
                  {/* [RGM] 2026-10-02 (reporte Rafael: "en el extracto no aparecen
                      cuotas abonadas ni pendientes — en el PDF sí"): mismas filas del
                      PDF (ExtractoPdfBuilder :134/:140) — los datos ya viajan en el
                      payload del extracto. */}
                  {fila('Cuotas Abonadas:', prestamo.prestamoCuotasAbonadas)}
                  {fila('Cuotas Pendientes:', prestamo.prestamoCuotasPendientes)}
                  {/* MPago número + texto (el GX imprime "Mpago: 7 Semanal" —
                      arptextracto.cs:304-310; el texto viene del mapeo
                      arptextracto.cs:210-229). */}
                  {fila('MPago:', `${prestamo.prestamoMPago} ${String(prestamo.mpagoTxt ?? '').trim()}`.trim())}
                  {fila('Dia Cobro:', prestamo.prestamoDiaCobro)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* [RGM] 2026-09-28 (reporte Rafael: la tarjeta "Abonado" salía en $0.00 —
            leía prestamoAbonos = A34, que son las CONDENAS, no los abonos reales;
            los abonos reales del préstamo son AbonosEfectivo = A33 — abono_bc.cs:760/:792). */}
        <div className="info-resumen-grid mt-3 mb-0">
          <ResumenCardPrestamo clase="info-creditos" etiqueta="Abonado" valor={fmtMoneda(prestamo.prestamoAbonosEfectivo)} />
          <ResumenCardPrestamo clase="info-atrasos" etiqueta="Atrasos" valor={String(prestamo.prestamoAtrazos ?? 0)} />
          <ResumenCardPrestamo clase="info-creditos" etiqueta="Condenas" valor={String(prestamo.prestamoAtrazosCondenados ?? 0)} />
        </div>
        {/* [RGM] 2026-09-24 (reporte Rafael: "en extracto no aparece pico, ni
            saldo condenas, ni saldo real; condenas en # no en $"): tarjetas
            con los datos que el backend YA envía — Pico, Saldo Condenas,
            # de Condenas (cantidad, no dinero) y Saldo Real.
            [RGM] 2026-09-28 (reorg pedida: "abonos atrasos condenas pico
            saldo prestamo saldo condenas saldo real (total)"): fila 1 =
            Abonado/Atrasos/Condenas; fila 2 = Pico/Saldos, con el Saldo
            Real (el TOTAL = Saldo + Saldo Condenas) de último. */}
        <div className="info-resumen-grid mt-3 mb-0">
          <ResumenCardPrestamo clase="info-creditos" etiqueta="Pico" valor={fmtMoneda(prestamo.prestamoSaldoPico)} />
          {/* [RGM] 2026-09-24 (pedido Rafael): "Prestamo Saldo" (etiqueta GX
              arptextracto :277) para distinguirlo del Saldo Real. */}
          <ResumenCardPrestamo clase="info-abonos" etiqueta="Prestamo Saldo" valor={fmtMoneda(prestamo.prestamoSaldo)} />
          <ResumenCardPrestamo clase="info-abonos" etiqueta="Saldo Condenas" valor={fmtMoneda(prestamo.prestamoSaldoCondenas)} />
          <ResumenCardPrestamo clase="info-atrasos" etiqueta="Saldo Real" valor={fmtMoneda(prestamo.prestamoSaldoReal)} />
        </div>

        <h6 className="fw-bold text-primary mt-3 mb-2">HISTORIAL DE ABONOS ({abonos.length})</h6>
        <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
          <table className="table table-sm table-bordered factura-tabla mb-0" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Nro</th><th>Fecha</th><th className="text-end">Valor Abono</th><th>Abono Tipo</th>
                {/* Detalle GX (arptextracto.cs — "DETALLE RECIBOS"): Abono a
                    Capital (Valor − utilidad) · Abono a Interes (la utilidad —
                    AbonoLibreValor1) · Nuevo Saldo AL FINAL (AbonoLibreValor2 —
                    el orden del reporte GX, decisión Rafael) */}
                <th className="text-end">Abono a Capital</th>
                <th className="text-end">Abono a Interes</th>
                <th className="text-end">Nuevo Saldo</th>
              </tr>
            </thead>
            <tbody>
              {abonos.map((a: any, i: number) => (
                <tr key={i}>
                  <td>{a.abonoNro}</td>
                  <td>{fecha(a.abonoFecha)}</td>
                  <td className="text-end">{fmtMoneda(a.abonoValor)}</td>
                  <td>{a.abonoTipoAbono}</td>
                  <td className="text-end">{fmtMoneda(a.abonoCapital)}</td>
                  <td className="text-end">{fmtMoneda(a.abonoInteres)}</td>
                  <td className="text-end fw-semibold">{fmtMoneda(a.abonoLibreValor2)}</td>
                </tr>
              ))}
              {abonos.length === 0 && <tr><td colSpan={7} className="text-center text-secondary">Sin abonos</td></tr>}
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>
  )
}

function ResumenCardPrestamo({ clase, etiqueta, valor }: { clase: string; etiqueta: string; valor: string }) {
  return (
    <div className={`info-resumen-card ${clase}`}>
      <div>
        <div className="info-etiqueta">{etiqueta}</div>
        <div className="info-valor">{valor}</div>
      </div>
    </div>
  )
}

export default PrestamosPage
